import { useRouter } from "next/router";
import React from "react";
import { useToast } from "../../../context/toastContext";
import Loading from "../Loading";
import {
  getProduct,
  getProductStripeCheckoutSession,
} from "../../../lib/products";
import Button from "../Buttons/Button";
import { getStripe } from "../../../lib/stripeRequest";

type Products = {
  id: number;
  quantity: number;
  name: string;
  price_in_cents: number;
};

export const Products = () => {
  const router = useRouter();
  const toast = useToast();
  const { slug } = router.query;
  const firstLoad = React.useRef(true);
  const [loading, setLoading] = React.useState(true);
  const [summary, setSummary] = React.useState("");
  const [products, setProducts] = React.useState([]);
  const [selectedProducts, setSelectedProducts] = React.useState<Products[]>(
    []
  );
  const [redirecting, setRedirecting] = React.useState(false);

  React.useEffect(() => {
    if (router.isReady && firstLoad.current) {
      if (slug?.[0]) {
        fetchData();
        setLoading(false);
      }
      firstLoad.current = false;
    }

    async function fetchData() {
      try {
        const { data } = await getProduct({ slug: slug?.[0] });
        setSummary(data.product_group.summary);
        setProducts(data.products);
        setLoading(false);
      } catch (error) {
        if (error.data?.error?.includes("not found")) {
          router.replace("/404");
        }
        toast("error", error?.data?.error);
      }
    }
  }, [slug, router.isReady, toast]);

  React.useEffect(() => {
    const handlePageShow = () => {
      // Reset the loading state if the page is restored from bfcache
      setRedirecting(false); // This resets the loading state
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const itemPerProduct = (product) => {
    const quantityByProduct =
      product.max_per_transaction > product.available_quantity
        ? product.available_quantity
        : product.max_per_transaction;

    return Array.from({ length: quantityByProduct }, (_, i) => (
      <option key={i} value={i + 1}>
        {i + 1}
      </option>
    ));
  };

  const handleOnClick = React.useCallback(async () => {
    try {
      setRedirecting(true);
      const { data } = await getProductStripeCheckoutSession({
        products: selectedProducts,
        product_group_slug: slug?.[0],
      });
      const stripe = await getStripe();
      localStorage.setItem('isLoading', 'true');
      await stripe.redirectToCheckout({ sessionId: data.id });
      setRedirecting(false);
    } catch (err) {
      toast("error", err.data.error);
      setRedirecting(false);
    }
  }, [selectedProducts, slug, toast]);

  const handleSelectedProducts = React.useCallback((product, quantity) => {
    setSelectedProducts((prev) => {
      const quantityNumber = +quantity;
      if (quantityNumber === 0) {
        return prev.filter((item) => item.id !== product.id);
      } else if (prev.find((item) => item.id === product.id)) {
        return prev.map((item) => {
          if (item.id === product.id) {
            return { ...item, quantity: quantityNumber };
          }
          return item;
        });
      }
      return [
        ...prev,
        {
          id: product.id,
          quantity: quantityNumber,
          name: product.name,
          price_in_cents: product.price_in_cents,
          premium_shipping_fee_in_cents: product.premium_shipping_fee_in_cents,
          shipping_fee_in_cents: product.shipping_fee_in_cents,
          max_per_transaction:
            product.max_per_transaction > product.available_quantity
              ? product.available_quantity
              : product.max_per_transaction,
        },
      ];
    });
  }, []);

  return loading ? (
    <Loading isLoading={loading} />
  ) : (
    <div className="mr-10">
      <div dangerouslySetInnerHTML={{ __html: summary }}></div>
      <div className="relative mt-4">
        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
          <thead className="text-xs text-gray-700 uppercase">
            <tr>
              <th scope="col" className="px-6 py-3">
                Name
              </th>
              <th scope="col" className="px-6 py-3">
                Quantity
              </th>
              <th scope="col" className="px-6 py-3">
                Price
              </th>
            </tr>
          </thead>
          <tbody>
            {!loading &&
              products
                .sort((a, b) => a.display_order - b.display_order)
                .map((product) => (
                  <tr className="bg-white border-b" key={product.id}>
                    <th
                      scope="row"
                      style={{ maxWidth: "400px" }}
                      className="px-6 py-4 font-medium text-gray-900"
                    >
                      {" "}
                      {product.name}{" "}
                    </th>
                    <td className="px-6 py-4 w-36">
                      {product.available_quantity === 0 ? (
                        <p>SOLD OUT</p>
                      ) : (
                        <select
                          id="products"
                          className="bg-gray-50 border border-gray-300 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 text-black"
                          onChange={(e) => {
                            handleSelectedProducts(
                              product,
                              e.currentTarget.value
                            );
                          }}
                        >
                          <option defaultValue={0}>0</option>
                          {itemPerProduct(product)}
                        </select>
                      )}
                    </td>
                    <td className="px-6 py-4 text-black">
                      ${product.price_in_cents / 100}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
        <div className="flex xs:justify-center md:justify-end ">
          <Button
            onClick={handleOnClick}
            type="Update"
            label="Checkout"
            className="xs:w-full md:w-1/3 mt-6"
            disabled={selectedProducts.length === 0 || redirecting}
            isLoading={redirecting}
          ></Button>
        </div>
      </div>
    </div>
  );
};
